import { LayoutDashboard, MapPin, GitCompare, Globe, Lightbulb, TrendingUp, Target, Eye } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const EnterprisePage = () => {
  const { t } = useLanguage();

  const capabilities = [
    {
      key: 'dashboards',
      icon: LayoutDashboard,
      title: t('enterprise.dashboards.title'),
      description: t('enterprise.dashboards.description'),
      features: t('enterprise.dashboards.features') || [],
      color: 'sky'
    },
    {
      key: 'hazard',
      icon: MapPin,
      title: t('enterprise.hazard.title'),
      description: t('enterprise.hazard.description'),
      features: t('enterprise.hazard.features') || [],
      color: 'rose'
    },
    {
      key: 'comparison',
      icon: GitCompare,
      title: t('enterprise.comparison.title'),
      description: t('enterprise.comparison.description'),
      features: t('enterprise.comparison.features') || [],
      color: 'emerald'
    },
    {
      key: 'regional',
      icon: Globe,
      title: t('enterprise.regional.title'),
      description: t('enterprise.regional.description'),
      features: t('enterprise.regional.features') || [],
      color: 'violet'
    },
    {
      key: 'insights',
      icon: Lightbulb,
      title: t('enterprise.insights.title'),
      description: t('enterprise.insights.description'),
      features: t('enterprise.insights.features') || [],
      color: 'amber'
    }
  ];

  const colorClasses = {
    sky: { bg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]' },
    rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' }
  };

  return (
    <div className="min-h-screen" data-testid="enterprise-page">
      {/* Hero */}
      <section className="gradient-hero pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-caption text-[#0077FF] mb-4 block">Enterprise Intelligence</span>
            <h1 className="text-h1 text-slate-900 mb-6" data-testid="enterprise-title">
              {t('enterprise.hero.title')}
            </h1>
            <p className="text-body-lg">
              {t('enterprise.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="section-padding py-16 bg-slate-900">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Eye, value: 'Real-Time', label: 'Incident Visibility' },
              { icon: Globe, value: 'Multi-Location', label: 'Network Monitoring' },
              { icon: TrendingUp, value: 'Trend', label: 'Pattern Analysis' },
              { icon: Target, value: 'Actionable', label: 'Intelligence' }
            ].map((stat, index) => (
              <div key={index}>
                <div className="w-12 h-12 mx-auto mb-3 bg-[#0077FF]/20 rounded-xl flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-[#4DB8FF]" />
                </div>
                <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-[#4DB8FF] text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-slate-900 mb-4">
              Intelligence Capabilities
            </h2>
            <p className="text-body-lg">
              Transform operational data into strategic advantage with comprehensive visibility and analytics.
            </p>
          </div>

          <div className="space-y-6">
            {capabilities.map((cap, index) => {
              const colors = colorClasses[cap.color];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={cap.key}
                  className="card-feature"
                  data-testid={`enterprise-capability-${cap.key}`}
                >
                  <div className={`grid md:grid-cols-2 gap-8 items-center ${!isEven ? 'md:flex-row-reverse' : ''}`}>
                    <div className={!isEven ? 'md:order-2' : ''}>
                      <div className={`inline-flex w-14 h-14 ${colors.bg} rounded-xl items-center justify-center mb-4`}>
                        <cap.icon className={`w-7 h-7 ${colors.text}`} />
                      </div>
                      <h3 className="text-h3 text-slate-900 mb-3">
                        {cap.title}
                      </h3>
                      <p className="text-body mb-6">
                        {cap.description}
                      </p>
                    </div>
                    <div className={!isEven ? 'md:order-1' : ''}>
                      <div className="grid grid-cols-2 gap-3">
                        {cap.features.map((feature, fIndex) => (
                          <div
                            key={fIndex}
                            className="bg-slate-50 rounded-lg p-4 border border-slate-100"
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-5 h-5 ${colors.bg} rounded flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                <span className={`text-xs font-bold ${colors.text}`}>{fIndex + 1}</span>
                              </div>
                              <span className="text-slate-700 text-sm font-medium">{feature}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Dashboard Preview */}
      <section className="section-padding bg-slate-50">
        <div className="container-max">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-caption text-[#0077FF] mb-4 block">Dashboard Experience</span>
              <h2 className="text-h2 text-slate-900 mb-4">
                Unified Operational View
              </h2>
              <p className="text-body-lg mb-6">
                Access real-time operational intelligence across your entire network from a single, intuitive interface designed for enterprise operations teams.
              </p>
              <ul className="space-y-3">
                {['Live incident feeds', 'Multi-location overview', 'Custom analytics', 'Export capabilities'].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${index === 0 ? 'bg-emerald-100' : 'bg-[#0077FF]/10'}`}>
                      <svg className={`w-3.5 h-3.5 ${index === 0 ? 'text-emerald-600' : 'text-[#0077FF]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="/screenshot_laptop_dashboard.png"
                alt="Tapelligence Command Center Dashboard"
                className="w-full h-auto rounded-2xl shadow-elevated"
                data-testid="enterprise-dashboard-img"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnterprisePage;
